/**
 * SmartSlot Matching Service
 */

import { getApiClient } from '../client';
import { useWorkbenchStore } from '../../stores/workbench';
import { playlistService } from './playlists';
import { useTrackMetadataStore } from '../../stores/trackMetadata';

/**
 * Match source types for SmartSlot discovery
 */
export type MatchSource =
  | { type: 'workbench' }
  | { type: 'playlist'; playlistId: string }
  | { type: 'playlists'; playlistIds: string[] }
  | { type: 'liked' }
  | { type: 'all' };

/**
 * Configuration for SmartSlot matching (new API)
 */
export interface SmartSlotServiceConfig {
  limit?: number;
  minScore?: number;
}

export interface SlotCriteriaRequest {
  bpm?: { min: number; max: number };
  keys?: string[];
  energy?: { min: number; max: number };
  valence?: { min: number; max: number };
  danceability?: { min: number; max: number };
}

export interface TrackMatch {
  trackId: string;
  trackUri: string;
  name: string;
  artist: string;
  album: string | null;
  score: number;
  breakdown: {
    bpm: number;
    key: number;
    energy: number;
    valence: number;
    danceability: number;
    total: number;
  };
}

export interface SmartSlotMatchResponse {
  matches: TrackMatch[];
  totalCandidates: number;
  queryUsed: string;
}

export const smartSlotService = {
  /**
   * Find tracks matching SmartSlot criteria
   *
   * NO SPOTIFY API CALLS - uses cached audio features only!
   *
   * Supports both legacy and new API:
   * - Legacy: findMatches(criteria, trackIds, limit, minScore)
   * - New: findMatches(criteria, source, config)
   *
   * @param criteria - Slot matching criteria (BPM, keys, energy, etc.)
   * @param sourceOrTrackIds - MatchSource (new) or track IDs array (legacy)
   * @param limitOrConfig - Limit number (legacy) or config object (new)
   * @param minScore - Minimum score threshold (legacy only)
   */
  async findMatches(
    criteria: SlotCriteriaRequest,
    sourceOrTrackIds: MatchSource | string[],
    limitOrConfig?: number | SmartSlotServiceConfig,
    minScore?: number
  ): Promise<SmartSlotMatchResponse> {
    // Detect old signature (array of track IDs)
    if (Array.isArray(sourceOrTrackIds)) {
      // Legacy call - use track IDs directly
      const trackIds = sourceOrTrackIds;
      const limit = limitOrConfig as number || 50;
      const score = minScore || 40;

      return getApiClient().post<SmartSlotMatchResponse>('/smart-slots/matches', {
        criteria,
        track_ids: trackIds,
        limit,
        min_score: score,
      });
    }

    // New signature - resolve source to track IDs
    const source = sourceOrTrackIds as MatchSource;
    const config = (limitOrConfig as SmartSlotServiceConfig) || {};
    const { limit = 50, minScore: score = 40 } = config;

    const trackIds = await this._resolveSourceToTrackIds(source);

    return getApiClient().post<SmartSlotMatchResponse>('/smart-slots/matches', {
      criteria,
      track_ids: trackIds,
      limit,
      min_score: score,
    });
  },

  /**
   * Resolve a MatchSource to an array of track IDs
   *
   * @private
   * @param source - Source specification
   * @returns Array of Spotify track IDs
   */
  async _resolveSourceToTrackIds(source: MatchSource): Promise<string[]> {
    switch (source.type) {
      case 'workbench': {
        const { tracks } = useWorkbenchStore.getState();
        return tracks.map((t) => t.id);
      }

      case 'playlist': {
        const playlist = await playlistService.get(source.playlistId);
        // Cache track metadata for later display
        const tracks = playlist.tracks.items.map((pt) => pt.track);
        useTrackMetadataStore.getState().addTracks(tracks);
        return tracks.map((t) => t.id);
      }

      case 'playlists': {
        const playlists = await Promise.all(
          source.playlistIds.map((id) => playlistService.get(id))
        );
        // Cache track metadata for all tracks from all playlists
        const allTracks = playlists.flatMap((pl) =>
          pl.tracks.items.map((pt) => pt.track)
        );
        useTrackMetadataStore.getState().addTracks(allTracks);

        // Deduplicate track IDs
        const allTrackIds = allTracks.map((t) => t.id);
        return Array.from(new Set(allTrackIds));
      }

      case 'liked':
        throw new Error('Liked Songs search not yet implemented');

      case 'all':
        throw new Error('Search all playlists not yet implemented');

      default:
        throw new Error(`Unknown source type: ${(source as any).type}`);
    }
  },
};
